// src/contexts/TenantContext.jsx
/**
 * TenantContext
 *
 * Fetches branding config from /api/tenant/ on app load.
 * Applies CSS variables so the entire UI adapts to the partner's brand.
 *
 * Usage:
 *   const { tenant, branding, loading } = useTenant();
 *
 * Notes:
 *   - tenant:        full tenant object { slug, name, is_aggregator, branding }
 *   - branding:      shortcut to tenant.branding (never undefined, defaults to {})
 *   - loading:       true until the API call resolves
 *   - tenantMissing: the host is a subdomain with no tenant behind it
 *
 * ON tenantMissing
 *   TenantMiddleware returns 404 for a host inside the tenant namespace that
 *   matches no active Tenant. Since the wildcard certificate and wildcard DNS
 *   mean ANY *.zelospace.com host reaches us, that is the only way to tell an
 *   unregistered subdomain from a real one.
 *
 *   ONLY a 404 sets this. A network failure, a timeout or a 5xx must not:
 *   showing "this space does not exist" to a real tenant's users because
 *   their connection blipped is worse than the blank page it replaces. Those
 *   keep the existing behaviour of falling back to default branding.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { getTenant } from '@/services/apiService';

const TenantContext = createContext(null);

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantMissing, setTenantMissing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getTenant();
        setTenant(data);

        // Apply branding as CSS variables on <html>
        // Partner frontends get their colours/fonts automatically.
        // Zelospace default (empty branding) leaves CSS vars untouched.
    const { branding = {} } = data;
    const root = document.documentElement;

        if (branding.primary_colour)
          root.style.setProperty('--colour-mikado-yellow', branding.primary_colour);
        if (branding.accent_colour)
          root.style.setProperty('--colour-imperial-red', branding.accent_colour);
        if (branding.bg_colour)
          root.style.setProperty('--colour-stone-brown', branding.bg_colour);
        if (branding.font_family)
          root.style.setProperty('--font-family', branding.font_family);
      } catch (err) {
        // A 404 is the middleware telling us this host has no tenant. Any
        // other failure - offline, timeout, 5xx - is NOT that, and falls back
        // to default branding as before rather than claiming the space does
        // not exist.
        if (err?.response?.status === 404) {
          setTenantMissing(true);
        } else {
          console.warn('[TenantContext] Could not load tenant branding:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    // ✅ branding exposed separately so callers can do:
    //    const { branding } = useTenant()
    //    instead of tenant?.branding
    <TenantContext.Provider value={{
      tenant,
      branding: tenant?.branding ?? {},
      loading,
      tenantMissing,
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);